import React from 'react';
import { XIcon, FactoryIcon } from 'lucide-react';
import Card from '../ui/Card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
interface PlantLevelViewProps {
  data: any;
  onClose: () => void;
}
const PlantLevelView: React.FC<PlantLevelViewProps> = ({
  data,
  onClose
}) => {
  const plantData = {
    name: data?.region || 'North',
    plants: [{
      name: 'Plant A',
      capacity: 85,
      production: 72,
      efficiency: 92
    }, {
      name: 'Plant B',
      capacity: 92,
      production: 88,
      efficiency: 96
    }, {
      name: 'Plant C',
      capacity: 78,
      production: 65,
      efficiency: 83
    }],
    monthlyProduction: [{
      month: 'Jan',
      actual: 2250,
      target: 2400
    }, {
      month: 'Feb',
      actual: 2380,
      target: 2400
    }, {
      month: 'Mar',
      actual: 2420,
      target: 2500
    }, {
      month: 'Apr',
      actual: 2350,
      target: 2500
    }, {
      month: 'May',
      actual: 2480,
      target: 2600
    }, {
      month: 'Jun',
      actual: 2520,
      target: 2600
    }]
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6" data-id="element-11372">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" data-id="element-11373">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-11374">
          <div className="flex items-center" data-id="element-11375">
            <FactoryIcon size={24} className="text-blue-600 mr-3" data-id="element-11376" />
            <div data-id="element-11377">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-11378">
                Plant-Level View: {plantData.name} Region
              </h2>
              <p className="text-sm text-gray-600" data-id="element-11379">
                Detailed production and efficiency metrics
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-11380">
            <XIcon size={24} className="text-gray-500" data-id="element-11381" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-11382">
          {/* Plant KPIs */}
          <div className="grid grid-cols-3 gap-4" data-id="element-11383">
            {plantData.plants.map((plant, index) => <Card key={index} className="p-4 bg-gradient-to-br from-blue-50 to-white" data-id="element-11384">
                <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-11385">
                  {plant.name}
                </h3>
                <div className="space-y-2" data-id="element-11386">
                  <div className="flex justify-between text-sm" data-id="element-11387">
                    <span className="text-gray-600" data-id="element-11388">Capacity Utilization</span>
                    <span className="font-bold text-blue-600" data-id="element-11389">
                      {plant.capacity}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-11390">
                    <span className="text-gray-600" data-id="element-11391">Production Rate</span>
                    <span className="font-bold text-green-600" data-id="element-11392">
                      {plant.production}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-11393">
                    <span className="text-gray-600" data-id="element-11394">Efficiency</span>
                    <span className="font-bold text-purple-600" data-id="element-11395">
                      {plant.efficiency}%
                    </span>
                  </div>
                </div>
              </Card>)}
          </div>
          {/* Charts */}
          <div className="grid grid-cols-2 gap-6" data-id="element-11396">
            <Card className="p-6" data-id="element-11397">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11398">
                Plant Performance Comparison
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11399">
                <BarChart data={plantData.plants} data-id="element-11400">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-11401" />
                  <XAxis dataKey="name" tick={{
                  fontSize: 12
                }} data-id="element-11402" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-11403" />
                  <Tooltip data-id="element-11404" />
                  <Legend data-id="element-11405" />
                  <Bar dataKey="capacity" fill="#3b82f6" name="Capacity %" data-id="element-11406" />
                  <Bar dataKey="efficiency" fill="#10b981" name="Efficiency %" data-id="element-11407" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6" data-id="element-11408">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11409">
                Monthly Production Trend
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11410">
                <LineChart data={plantData.monthlyProduction} data-id="element-11411">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-11412" />
                  <XAxis dataKey="month" tick={{
                  fontSize: 12
                }} data-id="element-11413" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-11414" />
                  <Tooltip data-id="element-11415" />
                  <Legend data-id="element-11416" />
                  <Line type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={2} name="Actual" data-id="element-11417" />
                  <Line type="monotone" dataKey="target" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Target" data-id="element-11418" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>
          {/* Action Items */}
          <Card className="p-6 bg-yellow-50 border-2 border-yellow-200" data-id="element-11419">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-11420">
              Action Items
            </h3>
            <ul className="space-y-2 text-sm" data-id="element-11421">
              <li className="flex items-start" data-id="element-11422">
                <span className="text-yellow-600 mr-2" data-id="element-11423">•</span>
                <span data-id="element-11424">
                  Plant C efficiency below target - investigate maintenance
                  schedule
                </span>
              </li>
              <li className="flex items-start" data-id="element-11425">
                <span className="text-yellow-600 mr-2" data-id="element-11426">•</span>
                <span data-id="element-11427">
                  Consider capacity expansion in Plant B to meet demand
                </span>
              </li>
              <li className="flex items-start" data-id="element-11428">
                <span className="text-yellow-600 mr-2" data-id="element-11429">•</span>
                <span data-id="element-11430">
                  Implement best practices from Plant B across all facilities
                </span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>;
};
export default PlantLevelView;